import { useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ServiceDialog } from "@/components/sites/bookings/ServicesPanel";
import BookingWizard from "./booking/BookingWizard";
import { ArchivedServices, DeleteServiceDialog } from "./booking/ServiceArchive";
import { useEditor } from "./useEditorState";

const summary = (x) => x.unlimited ? `arrivi ogni ${x.slot_step_min || 30} min · senza limiti` : x.covers ? `${x.covers} coperti · occupazione ${x.duration_min} min`
  : ["table", "request"].includes(x.booking_type) && !x.staff_ids?.length && !x.resource_ids?.length ? `arrivi ogni ${x.slot_step_min || x.duration_min} min · max ${x.parallel || 1} per orario`
  : `${x.duration_min} min${x.parallel > 1 ? ` · ${x.parallel} per orario` : ""}${x.capacity > 1 ? ` · ${x.capacity} posti` : ""}`;

const INIT = {
  table: { name: "Tavolo", duration_min: 90, capacity: 40, max_party: 10, min_notice_min: 60 },
  single: { name: "Consulenza", duration_min: 30 },
  multi: {},
};

export const pickFixed = (services, mode) => (mode === "table" ? services.find((x) => x.max_party > 1) : null) || services[0];

export default function BookingServicesList({ c, s, mode, setProps, onReload }) {
  const ed = useEditor();
  const [edit, setEdit] = useState(null);
  const [wiz, setWiz] = useState(false);
  const [del, setDel] = useState(null);
  const [tick, setTick] = useState(0);
  const ids = s.props.serviceIds || [];
  const fixed = mode !== "multi";
  const used = (x) => (fixed ? (ids[0] ? ids[0] === x.id : pickFixed(c.services, mode)?.id === x.id) : !ids.length || ids.includes(x.id));
  const toggle = (x) => {
    if (fixed) return setProps({ serviceIds: [x.id] });
    const all = c.services.map((y) => y.id);
    const cur = ids.length ? ids : all;
    const next = cur.includes(x.id) ? cur.filter((i) => i !== x.id) : [...cur, x.id];
    setProps({ serviceIds: next.length === all.length ? [] : next });
  };
  const saved = (svc) => {
    const isNew = svc?.id && !edit.svc;
    setEdit(null);
    setProps(isNew ? { serviceIds: fixed ? [svc.id] : ids.length ? [...ids, svc.id] : [] } : {});  // one props update (rev refreshes the preview)
    onReload();
  };
  const wizDone = (svc, m) => {
    setWiz(false);
    const first = !c.services.length;  // the widget mode is chosen only with the first service; later services never switch it
    const multi = (first ? m : mode) === "multi";
    setProps({ ...(first ? { mode: m } : {}), serviceIds: multi ? (ids.length ? [...ids, svc.id] : []) : [svc.id] });
    onReload();
  };
  const bumpAll = () => {  // every booking widget in the draft refetches its catalogue (deleted/restored services)
    const r = Date.now();
    (ed.content?.pages || []).forEach((p) => (p.sections || []).forEach((x) => { if (x.props && "bookingKey" in x.props && x.id !== s.id) ed.updateSection(x.id, { props: { ...x.props, rev: r } }); }));
  };
  const removed = (id) => {
    setDel(null); setTick((t) => t + 1);
    setProps(ids.includes(id) ? { serviceIds: ids.filter((i) => i !== id) } : {});
    bumpAll(); onReload();
  };
  const kind = mode === "table" ? "resource" : "staff";
  const pre = c.units.filter((u) => u.kind === kind).map((u) => u.id);
  const init = { ...INIT[mode], site_ids: [c.site.id], ...(pre.length ? { [kind === "resource" ? "resource_ids" : "staff_ids"]: pre.slice(0, 1) }
    : c.units[0] ? { [c.units[0].kind === "resource" ? "resource_ids" : "staff_ids"]: [c.units[0].id] } : {}) };
  return (
    <div className="space-y-1.5" data-testid="bw-services">
      <p className="text-[11px] text-slate-500">{fixed ? (mode === "table" ? "Servizio tavolo usato dal widget (coperti in sala)" : "Servizio prenotato dal widget") : "Servizi mostrati nel widget"}</p>
      {c.services.map((x) => (
        <div key={x.id} className="flex items-center gap-2 rounded-md border border-slate-200 px-2 py-1.5" data-testid={`bw-service-${x.id}`}>
          <input type={fixed ? "radio" : "checkbox"} checked={used(x)} onChange={() => toggle(x)} aria-label={`Usa ${x.name}`} data-testid={`bw-service-use-${x.id}`} />
          <div className="min-w-0 flex-1"><p className="truncate text-xs font-medium text-slate-800">{x.name}{x.active ? "" : " · non prenotabile"}</p>
            <p className="truncate text-[10px] text-slate-500">{summary(x)}{x.weekly ? " · orari propri" : ""}{x.fields?.length ? ` · ${x.fields.length} campi` : ""}</p></div>
          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEdit({ svc: x })} aria-label={`Modifica ${x.name}`} title="Modifica servizio" data-testid={`bw-service-edit-${x.id}`}><Pencil size={12} /></Button>
          <Button size="icon" variant="ghost" className="h-7 w-7 text-slate-400 hover:text-red-600" onClick={() => setDel(x)} aria-label={`Elimina ${x.name}`} title="Elimina servizio" data-testid={`bw-service-delete-${x.id}`}><Trash2 size={12} /></Button>
        </div>))}
      {!c.services.length && <p className="text-[11px] text-slate-500" data-testid="bw-no-services">Nessun servizio ancora: creane uno qui.</p>}
      <Button size="sm" className="h-8 w-full text-xs" onClick={() => setWiz(true)} data-testid="bw-service-add"><Plus size={13} className="mr-1" />{c.services.length ? "Aggiungi servizio (procedura guidata)" : "Crea il primo servizio (procedura guidata)"}</Button>
      <ArchivedServices c={c} tick={tick} onRestored={() => { setTick((t) => t + 1); setProps({}); bumpAll(); onReload(); }} />
      {del && <DeleteServiceDialog c={c} svc={del} onClose={() => setDel(null)} onDone={removed} />}
      {wiz && <BookingWizard c={c} mode={mode} askMode={!c.services.length} onClose={() => setWiz(false)} onDone={wizDone} />}
      {edit && <ServiceDialog tid={c.tid} svc={edit.svc} units={c.units} unitKind={kind}
        init={init} onClose={() => setEdit(null)} onSaved={saved} />}
    </div>);
}
